import React, {useState, useMemo} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {labels} from '../../../../../../../util/utils';
import {controlType, entities} from '../../../../../../../util/uiconstants';
import {useCanvasFormMetadata} from '../../../../../../../util/customHooks';
import BodySpecialCardItem from '../BodySpecialCardItem';
import BodySpecialAccordionHeaderColumn from '../BodySpecialAccordionHeaderColumn';
import ItProcessItGcCount from '../Common/ItProcessItGcCount';
import ItProcessControlCount from '../Common/ItProcessControlCount';
import BodySpecialDeleteItProcess from '../BodySpecialDeleteItProcess';
import BodySpecialAccordionContentList from '../BodySpecialAccordionContentList';
import ISA315ItRiskAccordionContentItem from './ISA315ItRiskAccordionContentItem';

export const ISA315ItGCTestStrategyListItem = ({
	itProcessId,
	bodyId,
	onCreateUpdate,
	engagementId,
	documentId,
	documentEngagementId,
	openRelateModal
}) => {
	const [showDelete, setShowDelete] = useState(false);

	const itProcess = useSelector((state) => state.bodiesSpecial.itProcesses?.byId[itProcessId]);

	function calculateItGcCounterColor(itControlsList) {
		let textColor = 'var(--neutrals-1000)';
		if (itControlsList && itControlsList.length > 0) {
			for (let itControl of itControlsList) {
				if (itControl?.controlDesignEffectivenessTypeId === null) {
					textColor = 'var(--red-600)';
					break;
				}
			}
		}
		return textColor;
	}

	const {isTargetEngagement} = useCanvasFormMetadata();

	const config = [
		{
			render: (
				<StyledITProcess
					className={'ITProcessHeader'}
					header={labels.itProcessHeader}
					value={itProcess.itProcessName}
				/>
			)
		},
		{
			header: labels.controlsSelectedHeader,
			className: 'primary-entity__child-entity ControlsGroup1',
			render: [
				{
					render: (
						<StyledSubGroupColumnLeft
							header={labels.controlsApplicationHeader}
							itProcessId={itProcessId}
							bodyId={bodyId}
							hasControlTesting
							controlTypeId={controlType.itApplicationControl}
						/>
					)
				},
				{
					render: (
						<StyledSubGroupColumnRight
							header={labels.itDMHeader}
							itProcessId={itProcessId}
							bodyId={bodyId}
							hasControlTesting
							controlTypeId={controlType.itDependentManualControl}
						/>
					)
				}
			]
		},
		{
			className: 'primary-entity__child-entity ControlsGroup2',
			render: [
				{
					render: (
						<StyledSubGroupColumnLeft2
							itProcessId={itProcessId}
							header={labels.itgcHeader}
							onRender={calculateItGcCounterColor}
						/>
					)
				},
				{
					render: (
						<StyledSubGroupColumnRight2 itProcessId={itProcessId} header={labels.itTestingHeader} hasITControlTesting />
					)
				}
			]
		}
	];

	const columns = React.useMemo(() => {
		return [
			{title: labels.iTRisksAndITGCs, className: 'isa315-itrisk-itgc-item__first-column'},
			{title: labels.itDesignEffectivenessHeader, className: 'isa315-itrisk-itgc-item__second-column'},
			{title: labels.itTestingColumnHeader, className: 'isa315-itrisk-itgc-item__third-column'}
		];
	}, []);

	const dropdownOptions = useMemo(
		() => [
			{
				name: 'edit',
				render: isTargetEngagement ? <div>{labels.viewItProcess}</div> : <div>{labels.itProcessEditItProcess}</div>,
				onClick: () => {
					onCreateUpdate({
						entityId: entities.ItProcess,
						entityUId: itProcessId,
						entityName: itProcess.itProcessName
					});
				}
			},
			{
				name: 'delete',
				render: <div>{labels.deleteItProcess}</div>,
				onClick: () => setShowDelete(true)
			}
		],
		[isTargetEngagement, itProcess.itProcessName]
	);

	let itProcessRelatedItRisks;
	if (itProcess && itProcess.itRisks?.length > 0) {
		itProcessRelatedItRisks = itProcess.itRisks;
	}

	return (
		<StyledBodySpecialCardItem
			className="styledBodySpecialCard_Item"
			config={config}
			dropdownOptions={dropdownOptions}
			disabled={!itProcess.itControls.length > 0}
		>
			{itProcessRelatedItRisks && itProcessRelatedItRisks.length > 0 && (
				<BodySpecialAccordionContentList columns={columns}>
					{itProcessRelatedItRisks.map((itRiskId) => {
						return (
							<ISA315ItRiskAccordionContentItem
								openRelateModal={openRelateModal}
								engagementId={engagementId}
								documentId={documentId}
								bodyId={bodyId}
								documentEngagementId={documentEngagementId}
								itRiskId={itRiskId}
								key={itRiskId}
								itProcess={itProcess}
							/>
						);
					})}
				</BodySpecialAccordionContentList>
			)}
			{showDelete && (
				<BodySpecialDeleteItProcess
					engagementId={engagementId}
					bodyId={bodyId}
					itProcessId={itProcessId}
					itProcessName={itProcess.itProcessName}
					setIsLoading={() => {}}
					show={showDelete}
					onHide={() => setShowDelete(false)}
				/>
			)}
		</StyledBodySpecialCardItem>
	);
};

ISA315ItGCTestStrategyListItem.propTypes = {
	itProcessId: PropTypes.number.isRequired,
	bodyId: PropTypes.number,
	onCreateUpdate: PropTypes.func,
	engagementId: PropTypes.number,
	documentId: PropTypes.string,
	documentEngagementId: PropTypes.number
};

export default ISA315ItGCTestStrategyListItem;

const StyledITProcess = styled(BodySpecialAccordionHeaderColumn)`
	width: calc(100% - var(--px-475));
`;

const StyledSubGroupColumnLeft = styled(ItProcessControlCount)`
	width: var(--px-120);
	padding: 0 var(--px-10) 0 0 !important;
`;
const StyledSubGroupColumnRight = styled(ItProcessControlCount)`
	width: var(--px-80);
	padding: 0 0 0 var(--px-10) !important;
`;

const StyledSubGroupColumnLeft2 = styled(ItProcessItGcCount)`
	width: var(--px-100);
	padding: 0 0 0 var(--px-10) !important;
	.no-design-effectiveness {
		color: var(--red-600) !important;
	}
`;

const StyledSubGroupColumnRight2 = styled(ItProcessItGcCount)`
	width: var(--px-80);
	padding: 0 0 0 var(--px-10) !important;
`;

const StyledBodySpecialCardItem = styled(BodySpecialCardItem)`
	.card-without-accordion {
		margin-left: var(--px-35);
		margin-right: var(--px-3);
	}
	.isa315-itrisk-itgc-item__first-column {
		display: inline-block;
		width: 70%;
		padding-bottom: var(--px-10);
		padding-right: var(--px-40);
		font-size: var(--px-14);
		font-weight: 300;
		.bodySpecialAccordionContentListHeaderText {
			cursor: text !important;
		}
	}

	.isa315-itrisk-itgc-item__second-column {
		display: inline-block;
		width: 20%;
		padding-bottom: var(--px-10);
		font-size: var(--px-12);
		font-weight: 300;
		.bodySpecialAccordionContentListHeaderText {
			cursor: text !important;
		}
	}

	.isa315-itrisk-itgc-item__third-column {
		display: inline-block;
		width: 10%;
		padding-bottom: var(--px-10);
		font-size: var(--px-12);
		font-weight: 300;
		.bodySpecialAccordionContentListHeaderText {
			cursor: text !important;
		}
	}
`;
